import { normalizePlanId } from "@billing/catalog.js";
import {
  ACCOUNTS_EVENT,
  CANCEL_NOTICE_HOURS,
  applySandboxCheckout,
  deskBillingBundle,
  ensureDeskBillingRecord,
  money,
  planLabel,
  processDeskRenewal,
  revokeSubscriptionCancel,
  scheduleSubscriptionCancel,
  statusLabel,
  SUB_STATUSES,
} from "../accounts";
import { requestReactivate, getLifecycle, ACCOUNT_STATUS } from "../api/accountLifecycle";
import { activatePaidPlan } from "../auth/authOnboarding";
import { productLabel } from "../auth/packageAccess";
import { getSession, setSession } from "../auth/authService";
import { updateRow } from "../db";
import { billingFreezeSummary, resumeBillingForUser } from "./lifecycleBilling.js";

export { CANCEL_NOTICE_HOURS };

export function isLocalSandbox() {
  if (typeof window === "undefined") return false;
  return /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
}

export function formatRenewalDate(iso) {
  if (!iso) return "Not scheduled";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function billingSummaryForSession(session) {
  if (!session?.userId) return null;
  ensureDeskBillingRecord(session);
  const bundle = deskBillingBundle(session);
  if (!bundle) return null;
  const sub = bundle.subscription;
  const planId = sub?.plan_id || normalizePlanId(session.plan, session.seat_tier);
  return {
    productLabel: productLabel(session),
    planLabel: planLabel(planId),
    status: sub?.status || session.plan_status || "signup",
    statusLabel: statusLabel(SUB_STATUSES, sub?.status || session.plan_status || "signup"),
    renewalDate: sub?.renewal_date || "",
    amount: sub?.amount ?? 0,
    interval: sub?.interval || "month",
    paymentMethod: sub?.payment_method_ref || "Not on file",
    cancelAtPeriodEnd: Boolean(sub?.cancel_at_period_end),
    cancelEffectiveAt: sub?.cancel_effective_at || "",
    cancelRequestedAt: sub?.cancel_requested_at || "",
    billing: billingFreezeSummary(session.userId),
    invoices: (bundle.invoices || []).slice(0, 8),
    payments: (bundle.payments || []).slice(0, 8),
    subscription: sub,
    account: bundle.account,
  };
}

export function syncBillingToAuthSession(session, checkoutResult = {}) {
  if (!session?.userId) return session;
  const sub = checkoutResult.subscription;
  const planPatch = {
    plan_status: "active",
    ...(sub?.plan_id ? { plan: sub.plan_id } : {}),
  };
  const activated = activatePaidPlan(session.userId, planPatch);
  const next = activated || setSession({ ...getSession(), ...session, ...planPatch });
  try {
    updateRow("users", session.userId, {
      plan: planPatch.plan || session.plan,
      plan_status: "active",
    });
  } catch {
    /* journey row may be absent */
  }
  return next;
}

export function reactivateDeskViaPayment(session, checkoutResult = {}) {
  if (!session?.userId) return null;
  resumeBillingForUser(session.userId, { fromPayment: true });
  const life = getLifecycle(session.userId);
  if (life.status !== ACCOUNT_STATUS.active) {
    try {
      requestReactivate(session.userId, session, { viaPayment: true });
    } catch {
      /* cancelled outside grace may still need broker approval */
    }
  }
  return syncBillingToAuthSession(session, checkoutResult);
}

export function reactivateViaPaymentReturn({ userId, role = "agent" }) {
  if (!userId) return null;
  resumeBillingForUser(userId, { fromPayment: true });
  try {
    requestReactivate(userId, { userId, role }, { viaPayment: true });
  } catch {
    /* ignore */
  }
  return getLifecycle(userId);
}

export function runSandboxCheckout(session) {
  const result = applySandboxCheckout(session, {
    planId: normalizePlanId(session.plan, session.seat_tier),
  });
  if (!result || result.duplicate) return { result, session };
  const nextSession = reactivateDeskViaPayment(session, result) || syncBillingToAuthSession(session, result);
  return { result, session: nextSession };
}

export function runSandboxRenewal(session, options = {}) {
  const result = processDeskRenewal(session, options);
  if (result?.renewed) {
    const nextSession = syncBillingToAuthSession(session, result);
    return { ...result, session: nextSession };
  }
  return result;
}

export function requestCancelAtPeriodEnd(session, reason = "") {
  return scheduleSubscriptionCancel(session, { reason });
}

export function undoCancelAtPeriodEnd(session) {
  return revokeSubscriptionCancel(session);
}

export function subscribeAccountsListener(callback) {
  if (typeof window === "undefined") return () => {};
  const handler = () => callback();
  window.addEventListener(ACCOUNTS_EVENT, handler);
  return () => window.removeEventListener(ACCOUNTS_EVENT, handler);
}

export function formatMoney(value) {
  return money(value);
}
